import React, { useState, useEffect } from 'react';
import { Target, Eye, Heart, Award, Users, Factory, Leaf, TrendingUp, Globe } from 'lucide-react';
import { aboutText, aboutTextEN } from '../utils/mockData';

export const About = () => {
  const [language, setLanguage] = useState('tr');
  const content = language === 'tr' ? aboutText : aboutTextEN;

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-lime-600 to-lime-700">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center relative">
            {/* Language Toggle */}
            <div className="absolute top-0 right-0 flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-lg p-1">
              <button
                onClick={() => setLanguage('tr')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  language === 'tr' 
                    ? 'bg-white text-lime-600' 
                    : 'text-white hover:bg-white/10'
                }`}
              >
                TR
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  language === 'en' 
                    ? 'bg-white text-lime-600' 
                    : 'text-white hover:bg-white/10'
                }`}
              >
                EN
              </button>
            </div>

            <h1 className="text-5xl font-bold text-white mb-6">
              {content.title}
            </h1>
            <p className="text-2xl text-lime-100">
              {content.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-lime-600 mb-4">
                {content.headline}
              </h2>
            </div>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {content.description}
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {content.description2}
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {content.description3}
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {content.description4}
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {content.description5}
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {content.description6}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              <div className="p-6 bg-lime-50 rounded-xl text-center">
                <div className="text-3xl font-bold text-lime-600 mb-2">21+</div>
                <div className="text-sm text-gray-600">
                  {language === 'tr' ? 'Yıllık Deneyim' : 'Years Experience'}
                </div>
              </div>
              <div className="p-6 bg-lime-50 rounded-xl text-center">
                <div className="text-3xl font-bold text-lime-600 mb-2">500+</div>
                <div className="text-sm text-gray-600">
                  {language === 'tr' ? 'Mutlu Müşteri' : 'Happy Customers'}
                </div>
              </div>
              <div className="p-6 bg-lime-50 rounded-xl text-center">
                <div className="text-3xl font-bold text-lime-600 mb-2">15+</div>
                <div className="text-sm text-gray-600">
                  {language === 'tr' ? 'İhracat Ülkesi' : 'Export Countries'}
                </div>
              </div>
              <div className="p-6 bg-lime-50 rounded-xl text-center">
                <div className="text-3xl font-bold text-lime-600 mb-2">1M+</div>
                <div className="text-sm text-gray-600">
                  {language === 'tr' ? 'Aylık Kapasite' : 'Monthly Capacity'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-br from-lime-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-10 rounded-2xl shadow-lg border border-lime-100">
              <div className="w-16 h-16 bg-lime-100 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-lime-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {language === 'tr' ? 'Misyonumuz' : 'Our Mission'}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {content.mission}
              </p>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-lg border border-lime-100">
              <div className="w-16 h-16 bg-lime-100 rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-lime-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {language === 'tr' ? 'Vizyonumuz' : 'Our Vision'}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {content.vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'tr' ? 'Değerlerimiz' : 'Our Values'}
            </h2>
            <p className="text-xl text-gray-600">
              {language === 'tr' 
                ? 'Çalışma prensiplerimizi şekillendiren temel değerler'
                : 'Core values that shape our working principles'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {content.values.map((value, index) => {
              const icons = [Award, Users, Heart, TrendingUp];
              const IconComponent = icons[index];
              return (
                <div 
                  key={index}
                  className="group text-center p-8 bg-gradient-to-br from-lime-50 to-white rounded-2xl border border-lime-100 hover:shadow-xl transition-all hover:-translate-y-2"
                >
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:bg-lime-600 transition-colors shadow-lg">
                    <IconComponent className="w-8 h-8 text-lime-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Üretim Kapasitelerimiz
            </h2>
            <p className="text-xl text-gray-300">
              Modern teknoloji ve geniş üretim altyapımız
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Factory,
                title: 'Modern Tesis',
                desc: '10.000 m² kapalı alan'
              },
              {
                icon: Leaf,
                title: 'Sürdürülebilir',
                desc: '%100 geri dönüştürülebilir'
              },
              {
                icon: TrendingUp,
                title: 'Yüksek Kapasite',
                desc: '1M+ adet/ay üretim'
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/20 transition-all">
                <item.icon className="w-12 h-12 text-lime-400 mb-4" />
                <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
